import { InputStateHandler } from './InputStateHandler.js';

export class BrregInputSearchPair {
  static HandlerType = 'BrregInputSearchPair';
  static #dropDownBoxCssClass = 'BrregDropDownBox';

  /**
   * The form container element (usually a DIV)
   * @type {HTMLElement}
   */
  #formContainer;
  /**
   * @type {HTMLInputElement}
   */
  #inpName;
  /**
   * @type {HTMLInputElement|null}
   */
  #inpPostnummer = null;
  /**
   * @type {HTMLInputElement|null}
   */
  #inpPoststed = null;
  /**
   * @type {String}
   */
  #apiEndpoint;
  #localityEndpoint;

  #postNummerStateHandler = null;
  #postStedStateHandler = null;
  #orgNumberStateHandler;

  #dropDownContainer;

  #noResultsFoundText;
  #didYouMeanText;

  constructor(formContainer, aff) {
    if (!(formContainer instanceof HTMLElement))
      throw new Error('formContainer must be a HTMLElement!');

    this.#formContainer = formContainer;

    this.#inpName = this.#FindInputField(aff.inputFieldName);
    this.#SetInputAriaTags();

    this.#orgNumberStateHandler = new InputStateHandler(
      this.#FindInputField(aff.targetFieldName)
    );

    this.#inpPostnummer = aff.postNrFieldName
      ? this.#FindInputField(aff.postNrFieldName)
      : this.#TryFindInputField('Postnummer');

    this.#inpPoststed = aff.postStedFieldName
      ? this.#FindInputField(aff.postStedFieldName)
      : this.#TryFindInputField('Poststed');

    this.#apiEndpoint = aff.apiEndpoint;
    this.#localityEndpoint = aff.localityEndpoint;

    this.#inpName.addEventListener('change', ev =>
      this.#OnBrregNameInputChanged(ev)
    );
    this.#inpName.addEventListener('paste', ev =>
      this.#OnBrregNameInputChanged(ev)
    );
    this.#inpName.addEventListener('keyup', ev =>
      this.#OnBrregNameInputChanged(ev)
    );

    this.#dropDownContainer = this.#CreateDropDown();

    this.#SetZIndexOnCogField();

    if (this.#inpPostnummer !== null) {
      this.#postNummerStateHandler = new InputStateHandler(this.#inpPostnummer);
    }
    if (this.#inpPoststed !== null) {
      this.#postStedStateHandler = new InputStateHandler(this.#inpPoststed);
    }

    if (aff.noResultsFoundText)
      this.#noResultsFoundText = aff.noResultsFoundText;
    if (aff.didYouMeanText) this.#didYouMeanText = aff.didYouMeanText;
  }

  /**
   * @param {String} labelText
   * @returns {HTMLInputElement}
   */
  #FindInputField(labelText) {
    const label = this.#FindExactLabel(labelText);

    const inputField = document.getElementById(label.htmlFor);

    if (!(inputField instanceof HTMLInputElement))
      throw new Error('Label not for an input element!');

    return inputField;
  }

  /**
   * Like #FindInputField, but returns null when the field isn't in the form.
   * @param {String} labelText
   * @returns {HTMLInputElement|null}
   */
  #TryFindInputField(labelText) {
    try {
      return this.#FindInputField(labelText);
    } catch {
      return null;
    }
  }

  #SetInputAriaTags() {
    this.#SetAriaExpanded(false);
    this.#inpName.setAttribute('aria-autocomplete', 'list');
    this.#inpName.setAttribute('aria-controls', this.#GetDropdownId());
    this.#inpName.setAttribute('aria-haspopup', 'listbox');
  }

  /**
   * @param {String} labelText
   * @returns {HTMLLabelElement}
   */
  #FindExactLabel(labelText) {
    /**
     * @type {HTMLLabelElement[]}
     */
    const labels = this.#formContainer.querySelectorAll('form label[for]');
    // Strip zero-width characters (Cognito inserts U+200B after slashes for
    // line-breaking) and trim, to avoid false negatives on invisible chars
    const normalize = text => text.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const searchText = normalize(labelText);

    for (const lbl of labels) {
      // Strip leading non-letters (e.g. required-field markers)
      const text = normalize(lbl.textContent).replace(/^[^a-zA-ZæøåÆØÅ]+/, '');
      if (text.startsWith(searchText)) return lbl;
    }

    throw new Error(`Unable to find label element for "${labelText}"!`);
  }

  #SetZIndexOnCogField() {
    let elem = this.#inpName;

    while (elem != null && !elem.classList.contains('cog-field'))
      elem = elem.parentElement;

    if (elem != null) elem.style.zIndex = 1;
  }

  #CreateDropDown() {
    const element = document.createElement('ul');
    element.setAttribute('role', 'listbox');

    this.#inpName.parentElement.style.position = 'relative';
    this.#inpName.parentElement.appendChild(element);

    element.classList.add(BrregInputSearchPair.#dropDownBoxCssClass);
    element.id = this.#GetDropdownId();

    element.addEventListener('focusout', ev => this.#OnDropDownFocusOut(ev));

    return element;
  }

  #GetDropdownId() {
    if (!this.#inpName.id)
      throw new Error('Input does not have an id attribute!');

    return this.#inpName.id + '-BrregDropDown';
  }

  #OnDropDownFocusOut(ev) {
    if (!(ev instanceof FocusEvent)) return;

    if (!this.#IsPartOfDropdown(ev.relatedTarget))
      this.#DeleteAllDropDownOptions();
  }

  #IsPartOfDropdown(element) {
    if (!(element instanceof HTMLElement)) return false;

    while (element.parentElement) {
      if (element === this.#dropDownContainer) return true;

      if (
        element.hasAttribute('aria-controls') &&
        element.getAttribute('aria-controls') == this.#dropDownContainer.id
      )
        return true;

      element = element.parentElement;
    }

    return false;
  }

  #lastSearchValue;
  #abortController = new AbortController();

  #AbortAndReset() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  async #OnBrregNameInputChanged(ev) {
    const searchText = this.#inpName.value.trim();

    if (searchText.length == 0) {
      this.#lastSearchValue = '';
      this.#DeleteAllDropDownOptions();
      return;
    }

    if (searchText == this.#lastSearchValue) {
      return;
    }

    this.#lastSearchValue = searchText;

    const qs = new URLSearchParams();
    qs.append('text', searchText);

    const url = BrregInputSearchPair.#GetUrl(this.#apiEndpoint, qs);

    this.#AbortAndReset();

    const result = await fetch(url, {
      signal: this.#abortController.signal
    }).catch(e => {});

    if (result && result.status == 200) {
      this.#DisplayResults(await result.json());
    } else if (result && result.status == 204) {
      this.#DisplayNoResults();
    }
  }

  /**
   * Get a full URL to the endpoint
   * @param {string} endpoint
   * @param {URLSearchParams} qs
   */
  static #GetUrl(endpoint, qs) {
    let url = endpoint + '?' + qs.toString();

    if (window.location.host === 'localhost:6006')
      url = 'https://localhost:7178' + url;

    return url;
  }

  #DisplayResults(dic) {
    this.#DeleteAllDropDownOptions();
    this.#SetAriaExpanded(true);

    for (const row of dic) {
      this.#CreateOption(row.Number, row.Name);
    }
  }

  #GetNoResultsFoundText() {
    if (this.#noResultsFoundText && this.#noResultsFoundText.length > 0)
      return this.#noResultsFoundText.trim();

    return 'Ingen resultater funnet.';
  }

  #GetDidYouMeanText() {
    if (this.#didYouMeanText && this.#didYouMeanText.length > 0)
      return this.#didYouMeanText.trim();

    return 'Mente du en av disse?';
  }

  #DisplayNoResults() {
    if (this.#dropDownContainer.querySelector('.no-results') != null) {
      return;
    }

    const div = document.createElement('div');
    div.classList.add('no-results');
    div.innerText = this.#GetNoResultsFoundText();

    if (this.#dropDownContainer.hasChildNodes())
      div.innerText += ' ' + this.#GetDidYouMeanText();

    this.#dropDownContainer.insertBefore(
      div,
      this.#dropDownContainer.firstElementChild
    );
  }

  #CreateOption(orgNumber, orgName) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    li.appendChild(btn);
    li.setAttribute('role', 'option');

    btn.innerText = orgName + ' (' + orgNumber + ')';

    btn.addEventListener(
      'click',
      async ev => await this.#OnOptionClicked(orgNumber, orgName, ev)
    );

    this.#dropDownContainer.appendChild(li);
  }

  async #OnOptionClicked(orgNumber, orgName, ev) {
    this.#DeleteAllDropDownOptions();

    if (ev instanceof Event) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
    }

    this.#inpName.value = orgName;
    // Update the last search value before dispatching 'change', so the change
    // handler doesn't rerun the search and reopen the dropdown
    this.#lastSearchValue = orgName;
    this.#inpName.dispatchEvent(new Event('change'));

    this.#orgNumberStateHandler.TrySetValue(orgNumber);

    if (orgNumber) {
      const orgNum = Number.parseInt(orgNumber);

      if (
        this.#HasLocalityFields() &&
        orgNum &&
        orgNum > 0 &&
        this.#localityEndpoint
      ) {
        const qs = new URLSearchParams();
        qs.set('orgNumber', orgNum);
        const url = BrregInputSearchPair.#GetUrl(this.#localityEndpoint, qs);

        this.#abortController.abort();
        this.#abortController = new AbortController();

        const result = await fetch(url, {
          signal: this.#abortController.signal
        });

        if (result.status == 200) this.#ShowLocality(await result.json());
        else if (result.status == 204) this.#RemovePrefilledLocality();
      }
    }
  }

  #HasLocalityFields() {
    return this.#inpPostnummer !== null || this.#inpPoststed !== null;
  }

  #ShowLocality(jsonObj) {
    const { Postnummer, Poststed } = jsonObj;

    if (Postnummer && this.#inpPostnummer !== null) {
      this.#postNummerStateHandler.TrySetValue(Postnummer);
    }
    if (Poststed && this.#inpPoststed !== null) {
      this.#postStedStateHandler.TrySetValue(Poststed);
    }
  }

  #RemovePrefilledLocality() {
    if (this.#postNummerStateHandler !== null)
      this.#postNummerStateHandler.TrySetValue(null);
    if (this.#postStedStateHandler !== null)
      this.#postStedStateHandler.TrySetValue(null);
  }

  #SetAriaExpanded(state = true) {
    BrregInputSearchPair.#SetAriaExpandedState(this.#inpName, state);
  }

  /**
   * Set the aria-expanded attribute state of an element
   * @param {HTMLElement} elem
   * @param {boolean} state
   */
  static #SetAriaExpandedState(elem, state) {
    elem.setAttribute('aria-expanded', state);
  }

  #DeleteAllDropDownOptions() {
    this.#SetAriaExpanded(false);
    BrregInputSearchPair.#CloseAutoComplete(this.#dropDownContainer);
  }

  /**
   * Delete all the children and set the aria expanded state to false
   * @param {HTMLElement} element
   */
  static #CloseAutoComplete(element) {
    if (!(element instanceof HTMLElement)) return;

    if (element.previousElementSibling.hasAttribute('aria-expanded'))
      this.#SetAriaExpandedState(element.previousElementSibling, false);

    while (element.hasChildNodes()) {
      try {
        element.removeChild(element.firstElementChild);
      } catch (ev) {
        console.warn(ev);
      }
    }
  }

  static DeleteAllDropDownOptions(ev = null) {
    const found = document.body.querySelectorAll(
      '.' + this.#dropDownBoxCssClass
    );

    for (let x of found) this.#CloseAutoComplete(x);
  }

  static HandleKeyDown(ev) {
    if (!(ev instanceof Event)) return;

    if (ev.key == 'Escape' || ev.keyCode == 27) {
      this.DeleteAllDropDownOptions();
      return;
    }

    if (ev.key == 'ArrowDown' || ev.keyCode == 40) {
      this.#MoveDownFrom(ev.target, ev);
      return;
    }

    if (ev.key == 'ArrowUp' || ev.keyCode == 38) {
      this.#MoveUpFrom(ev.target, ev);
      return;
    }
  }

  static #IsInputWithAriaAutocompleteList(element) {
    return (
      element.hasAttribute('aria-controls') &&
      element.hasAttribute('aria-autocomplete') &&
      element.getAttribute('aria-autocomplete') == 'list'
    );
  }

  static #MoveDownFrom(element, ev) {
    if (!(element instanceof HTMLElement)) return;

    if (this.#IsInputWithAriaAutocompleteList(element)) {
      const targetList = document.getElementById(
        element.getAttribute('aria-controls')
      );

      this.#FocusFirstButton(targetList, ev);
      return;
    }

    if (this.#TryMoveDownIfDropdownListButton(element)) {
      ev.preventDefault();
      return;
    }
  }

  static #MoveUpFrom(element, ev) {
    if (!(element instanceof HTMLElement)) return;

    if (this.#TryMoveUpIfDropdownListButton(element)) {
      ev.preventDefault();
      return;
    }
  }

  static #FocusFirstButton(element, ev) {
    if (!(element instanceof HTMLElement)) return;

    const q = element.querySelector('button');

    if (q) {
      q.focus();
      ev.preventDefault();
    }
  }

  static #TryMoveDownIfDropdownListButton(element) {
    let parent = element.parentElement;

    while (parent) {
      if (parent.classList.contains(this.#dropDownBoxCssClass)) {
        break;
      }

      parent = parent.parentElement;
    }

    if (!parent) return false;

    const allButtons = parent.querySelectorAll('button');

    let takeNext = false;
    let targetButton = null;

    if (allButtons) {
      for (let btn of allButtons) {
        if (takeNext) {
          targetButton = btn;
          break;
        }

        if (btn === element) {
          takeNext = true;
        }
      }
    }

    if (!takeNext) return false;

    if (targetButton) {
      targetButton.focus();
    }

    return true;
  }

  static #TryMoveUpIfDropdownListButton(element) {
    let dropDownBox = element.parentElement;

    while (dropDownBox) {
      if (dropDownBox.classList.contains(this.#dropDownBoxCssClass)) {
        break;
      }

      dropDownBox = dropDownBox.parentElement;
    }

    if (!dropDownBox) return false;

    const allButtons = dropDownBox.querySelectorAll('button');

    let previousButton = null;

    if (allButtons) {
      for (let btn of allButtons) {
        if (btn === element) break;

        previousButton = btn;
      }
    }

    if (previousButton) {
      previousButton.focus();
      return true;
    }

    if (dropDownBox.hasAttribute('id')) {
      const targetInput = document.body.querySelector(
        'input[aria-controls="' + dropDownBox.id + '"]'
      );

      if (targetInput) {
        targetInput.focus();
        return;
      }
    }

    return true;
  }
}
